const $$splitComponentImporter = () => import('/Users/Javvad_1/Desktop/Petso_Dev/monorepo/petso-pro/src/routes/dashboard/team-chat.tsx?tsr-split=component');
import { lazyRouteComponent } from '@tanstack/react-router';
// ═══════════════════════════════════════════════════════════════════════════
// Team Chat — unified collaboration surface
// ═══════════════════════════════════════════════════════════════════════════
//
// Three-pane shell (sidebar · thread · right rail) built on the new
// /pro/businesses/:bId/chat/* backend. Supports:
//
//   - Channels, DMs, group chats, project chats, announcements
//   - Threaded replies with reply counts
//   - Pinned messages panel
//   - Files panel (all attachments in a conversation)
//   - Members panel with add/remove
//   - @mention autocomplete via /chat/directory
//   - Reactions (emoji toggle)
//   - Real-time updates via SSE (with polling fallback)
//   - Search modal (⌘K) with conversation/sender/date filters
//   - Read receipts via per-member lastReadAt
//   - New-message divider
//
// The file is intentionally kept as a single module during Phase 1 so the
// developer experience stays linear. Sub-components can be extracted later
// once the shape stabilizes.

import { createFileRoute } from '@tanstack/react-router';
import { type KeyboardEvent, type ChangeEvent, type FormEvent } from 'react';
import { Smile, AtSign, CheckCheck } from 'lucide-react';
import { type ChatStreamEvent } from '@/hooks/useChatStream';
export const Route = createFileRoute('/dashboard/team-chat')({
  component: lazyRouteComponent($$splitComponentImporter, 'component')
});

// ─── Constants ──────────────────────────────────────────────────────────────

// ─── Helpers ────────────────────────────────────────────────────────────────

// Download (or inline-preview) an attachment via the authenticated
// /pro/documents/:key/download endpoint. We fetch the bytes as a blob so the
// Authorization header is attached, then open the blob URL in a new tab.

// ─── Component ──────────────────────────────────────────────────────────────

// ═══════════════════════════════════════════════════════════════════════════
// MessageRow — single message row with hover actions & reactions
// ═══════════════════════════════════════════════════════════════════════════

interface MessageRowProps {
  msg: ChatMessage;
  currentUserId: string;
  canPin: boolean;
  editingMessageId: string | null;
  editingContent: string;
  setEditingMessageId: (id: string | null) => void;
  setEditingContent: (s: string) => void;
  onSaveEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onPin: (id: string) => void;
  onReact: (id: string, emoji: string) => void;
  onOpenThread: (msg: ChatMessage) => void;
  t: (key: string, fallback?: string) => string;
}
// ═══════════════════════════════════════════════════════════════════════════
// MembersPanel — list current members + add/remove controls
// ═══════════════════════════════════════════════════════════════════════════

interface MembersPanelProps {
  businessId: string;
  conversationId: string;
  directory: ChatDirectoryMember[];
  canManage: boolean;
  currentUserId: string;
  onMembersChanged: () => void;
  t: (key: string, fallback?: string) => string;
}